import { useProperty, useTranslation } from '@eti/providers';
import { ConnectedSelectBox } from '@eti/form';
import { useRef, useState } from 'react';
import { constants, price as priceUtils } from '@eti/utils';
import { Inline, SelectBox, Text } from '@etg/wings';
import { screenReaderOnly } from '../../../common/utils/styles';
import { getReadMoreLabel, getSingleChoiceProps } from '../../../common/utils/productCardProps';
import ProductLayout from '../../../common/main-layout/ProductLayout';
import getTranslatedTexts from '../../../common/text-resolvers/utils/getTranslatedTexts';
import { CABIN_BAGGAGE } from '../../../products';
import { cabinBaggagePurchasablePiecesWeightDimension, getTravelersPostbooking } from '../utils/cabinBaggageUtils';
import { formatLabel, formatOptions, getMinPrice, getTotalPrice, handleSelectedOption } from '../../../common/utils/selectBoxOptions';
import { useIllustration } from '../../../common/utils/useIllustration';
import { useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import { useMutationObserver } from '../../../custom-hooks/useMutationObserver';
import { useLinksAccessibility } from '../../../custom-hooks/useLinkAccessibility';
import { cabinBaggagePostbookingStyles } from './styles';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const generalTexts = {
  additionalInformation: 'cabinBaggagePerTravelerAdditionalInformation',
  allTravelersLabel: 'Product.BaggagePerTraveler.Dropdown.Label.Select.All',
  dimensionDescription: 'Product.CabinBaggagePerTraveler.Dimension.Description',
  dimensionValues: 'Product.CabinBaggagePerTraveler.Dimensions',
  fromPriceLabel: 'Products.Price.Label.From',
  headerDescription: 'cabinBaggagePerTravelerDescription',
  noTravelersLabel: 'Product.BaggagePerTraveler.Dropdown.Label.Select.None',
  onlyPriceLabel: 'Products.Price.Label.Only',
  piecesText: 'Product.BaggagePerTraveler.Pieces',
  placeholder: 'Product.BaggagePerTraveler.Dropdown.Placeholder.Text',
  placeholderSelectedPlural: 'Product.BaggagePerTraveler.Dropdown.Label.Selected.Plural',
  placeholderSelectedSingular: 'Product.BaggagePerTraveler.Dropdown.Label.Selected.Singular',
  promotionalText: 'cabinBaggagePerTravelerPromotionalText',
  readMoreContent: 'cabinBaggagePerTravelerReadMoreModalContent',
  selectboxLabel: 'Product.BaggagePerTraveler.Dropdown.Label.Text',
  title: 'cabinBaggagePerTravelerTitle',
  totalPriceLabel: 'Products.Price.Label.Total',
  weightValues: 'Product.BaggagePerTraveler.Weight',
  weightDescription: 'Product.BaggagePerTraveler.Weight.Description',
  dropDownCloseInstruction: 'ScreenReader.Product.CloseDropdown.Instruction.Text'
};
export const getBaggageWeight = _ref => {
  let {
    pieces,
    weight,
    weightUnit,
    t
  } = _ref;
  if (pieces && weight && weightUnit) {
    const {
      piecesText,
      weightDescription,
      weightValues
    } = generalTexts;
    const baggageWeightValues = `${t(piecesText, pieces)} ${t(weightValues, weight, weightUnit)}`;
    return t(weightDescription, baggageWeightValues);
  }
  return '';
};
export const getBaggageDimensions = _ref2 => {
  let {
    dimensions,
    t
  } = _ref2;
  const {
    height,
    length,
    width
  } = dimensions || {};
  if (height && length && width) {
    var _t;
    const {
      dimensionDescription,
      dimensionValues
    } = generalTexts;
    const baggageDimensionValues = (_t = t(dimensionValues, length, width, height)) === null || _t === void 0 ? void 0 : _t.replace(/[()]/g, '');
    return t(dimensionDescription, baggageDimensionValues);
  }
  return '';
};
const {
  cabinBaggageProductLayoutStyle,
  cabinBaggageBodyGridLayoutStyle,
  cabinBaggageCtaGridLayoutStyle,
  weightDescriptionStyles,
  dimensionGridStyle,
  connectedSelectBoxStyles,
  selectBoxOptionLabelWrapperStyles,
  selectBoxOptionLabelTextStyle
} = cabinBaggagePostbookingStyles();
const CabinBaggagePostbooking = _ref3 => {
  var _order$availableExtra;
  let {
    order
  } = _ref3;
  const [selectedCabinBagOptions, setSelectedCabinBagOptions] = useState([]);
  const [selectedSingleChoiceValue, setSelectedSingleChoiceValue] = useState(undefined);
  const {
    t,
    nonDebugT
  } = useTranslation();
  const {
    p
  } = useProperty();
  const rootElement = useRef(null);
  useProductSnowplowTracking(CABIN_BAGGAGE.name, {
    rootElement
  });
  const illustrationUrl = useIllustration(CABIN_BAGGAGE.name);
  const sellSpecification = (_order$availableExtra = order.availableExtraProducts.find(product => Number(product.id) === CABIN_BAGGAGE.id)) === null || _order$availableExtra === void 0 ? void 0 : _order$availableExtra.sellSpecification;
  const {
    dimensions,
    pieces,
    weight,
    weightUnit
  } = cabinBaggagePurchasablePiecesWeightDimension(sellSpecification);
  const {
    additionalInformation,
    allTravelersLabel,
    headerDescription,
    fromPriceLabel,
    noTravelersLabel,
    onlyPriceLabel,
    placeholder,
    placeholderSelectedPlural,
    placeholderSelectedSingular,
    promotionalText,
    readMoreContent,
    selectboxLabel,
    totalPriceLabel,
    title,
    dropDownCloseInstruction
  } = getTranslatedTexts(generalTexts, t);
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title
  });
  const travelers = getTravelersPostbooking(CABIN_BAGGAGE.id, order);
  const travelersWithoutInfants = travelers === null || travelers === void 0 ? void 0 : travelers.filter(_ref4 => {
    let {
      ageType
    } = _ref4;
    return ageType !== constants.ageTypes.INFANT;
  });
  const showSingleChoiceSelectButtons = (travelersWithoutInfants === null || travelersWithoutInfants === void 0 ? void 0 : travelersWithoutInfants.length) === 1;
  const cabinBagOptions = travelersWithoutInfants === null || travelersWithoutInfants === void 0 ? void 0 : travelersWithoutInfants.map(_ref5 => {
    let {
      id,
      name,
      price
    } = _ref5;
    return {
      id: String(id),
      name,
      priceRaw: price
    };
  });
  const formattedOptions = formatOptions(cabinBagOptions, allTravelersLabel, noTravelersLabel);
  const isSelected = selectedCabinBagOptions && selectedCabinBagOptions.length > 0;
  const getPriceLabel = () => {
    if (isSelected) {
      return totalPriceLabel;
    }
    return showSingleChoiceSelectButtons ? onlyPriceLabel : fromPriceLabel;
  };
  const productPriceRaw = isSelected ? getTotalPrice(selectedCabinBagOptions === null || selectedCabinBagOptions === void 0 ? void 0 : selectedCabinBagOptions.filter(option => !(option !== null && option !== void 0 && option.exclude))) : getMinPrice(cabinBagOptions);
  const extraProps = {
    additionalBaggageWeight: getBaggageWeight({
      pieces,
      weight,
      weightUnit,
      t
    }),
    additionalBaggageDimensions: getBaggageDimensions({
      dimensions,
      t
    }),
    ...(showSingleChoiceSelectButtons ? {
      ...getSingleChoiceProps(t, p, nonDebugT),
      normalize: value => {
        setSelectedSingleChoiceValue(value);
        if (value === 'true') {
          setSelectedCabinBagOptions(formattedOptions);
          return formattedOptions;
        }
        setSelectedCabinBagOptions([]);
        return [];
      },
      selectedValue: selectedSingleChoiceValue
    } : {})
  };
  const readMoreButtonLabel = getReadMoreLabel(t);
  const productData = {
    additionalInformation,
    extraProps,
    description: headerDescription,
    illustrationUrl,
    isSelected,
    price: priceUtils.formatPrice(productPriceRaw),
    priceLabel: getPriceLabel(),
    priceRaw: productPriceRaw !== null && productPriceRaw !== void 0 ? productPriceRaw : 0,
    productId: CABIN_BAGGAGE.id,
    productName: CABIN_BAGGAGE.name,
    promotionalText,
    readMoreButtonLabel,
    readMoreContent,
    title,
    isHighlighted: false,
    shouldDisplayUpsell: false
  };
  const formatSelectedOption = selectedOptions => {
    const label = formatLabel({
      selectedOptions,
      allTravelersLabel,
      defaultPlaceholder: placeholder,
      placeholderSelectedSingular,
      placeholderSelectedPlural,
      noTravelersLabel
    });
    return /*#__PURE__*/_jsx("p", {
      children: label
    });
  };

  // Add ARIA roles and attributes to enhance screen reader usability when the dropdown menu opens.
  useMutationObserver({
    targetElement: rootElement,
    selector: "[role='listbox']",
    onElementAdded: element => {
      element.setAttribute('aria-describedby', 'dropdown-close-instruction');
    }
  });
  return /*#__PURE__*/_jsxs(ProductLayout, {
    customGrid: cabinBaggageProductLayoutStyle(readMoreContent),
    productData: productData,
    ref: rootElement,
    children: [/*#__PURE__*/_jsx(ProductLayout.Body, {
      customGrid: cabinBaggageBodyGridLayoutStyle,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [extraProps.additionalBaggageWeight && /*#__PURE__*/_jsx("bdi", {
          className: weightDescriptionStyles,
          "data-testid": `${CABIN_BAGGAGE.name}-weight-description`,
          children: extraProps.additionalBaggageWeight
        }), extraProps.additionalBaggageDimensions && /*#__PURE__*/_jsx("bdi", {
          className: dimensionGridStyle,
          "data-testid": `${CABIN_BAGGAGE.name}-dimension-description`,
          children: extraProps.additionalBaggageDimensions
        })]
      })
    }), !showSingleChoiceSelectButtons ? /*#__PURE__*/_jsx(ProductLayout.Cta, {
      customGrid: cabinBaggageCtaGridLayoutStyle,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          "aria-hidden": true,
          className: screenReaderOnly,
          id: "dropdown-close-instruction",
          tabIndex: -1,
          children: dropDownCloseInstruction
        }), /*#__PURE__*/_jsx(ConnectedSelectBox, {
          className: connectedSelectBoxStyles,
          "data-testid": `${CABIN_BAGGAGE.name}-select-box`,
          formatSelectedOption: formatSelectedOption,
          isDisabled: !(formattedOptions !== null && formattedOptions !== void 0 && formattedOptions.length),
          isMultiple: true,
          label: selectboxLabel,
          name: `${CABIN_BAGGAGE.name}.value`,
          normalize: (newValue, prevVal) => {
            const selectedOption = handleSelectedOption(formattedOptions, prevVal, newValue);
            setSelectedCabinBagOptions(selectedOption);
            return selectedOption;
          },
          placeholder: placeholder,
          validationRuleName: CABIN_BAGGAGE.name,
          children: /*#__PURE__*/_jsx("ul", {
            children: formattedOptions === null || formattedOptions === void 0 ? void 0 : formattedOptions.map((option, index) => {
              var _String;
              return /*#__PURE__*/_jsx(SelectBox.Option, {
                "data-testid": `etiDropdownOption-${CABIN_BAGGAGE.name}-option-${(_String = String(option.value)) === null || _String === void 0 ? void 0 : _String.toLocaleLowerCase()}`,
                isMultiple: true,
                label: option.label ? /*#__PURE__*/_jsxs(Inline, {
                  className: selectBoxOptionLabelWrapperStyles,
                  children: [/*#__PURE__*/_jsx(Text, {
                    variant: index === 0 ? '14-bold' : '14',
                    children: option.label
                  }), /*#__PURE__*/_jsx(Text, {
                    className: selectBoxOptionLabelTextStyle,
                    "data-testid": `etiDropdownOption-${CABIN_BAGGAGE.name}-price-selection-${index}`,
                    variant: index === 0 ? '14-bold' : '14',
                    children: option.price
                  })]
                }) : '',
                value: option
              }, option.value);
            })
          })
        })]
      })
    }) : null]
  });
};
export default CabinBaggagePostbooking;
//# sourceMappingURL=CabinBaggagePostbooking.js.map